import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { StatisticGraph, getEdgeDates } from './components/StatisticGraph';
import { fetchStatistic, fetchPhraseList, fetchStatisticFull } from './api';
import infSvg from 'bootstrap-icons/icons/infinity.svg';
import calendarSvg from 'bootstrap-icons/icons/calendar-event.svg';

const dateDigits = (n) => {
  if (n.toString().length < 2) {
    return `0${n}`;
  }

  return n;
};

const dateToParam = (date) =>
  `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
const dateToInputValue = (date) =>
  `${date.getFullYear()}-${dateDigits(date.getMonth() + 1)}-${dateDigits(date.getDate())}`;

export const App = () => {
  const [isDatesUsed, setIsDatesUsed] = useState(true);
  const [phrasesList, setPhrasesList] = useState([]);
  const [statisticData, setStatisticData] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedPhraseId, setSelectedPhraseId] = useState('');

  const startDateStr = dateToInputValue(startDate);
  const endDateStr = dateToInputValue(endDate);

  const appContainer = document.getElementById('statistic-app');

  const containerWidth = appContainer.clientWidth;
  const containerHeight = window.innerHeight * 0.7;

  const fetchDataBySelectedDates = () => {
    const start = dateToParam(startDate);
    const end = dateToParam(endDate);
    fetchStatistic(start, end)
      .then((resp) => resp.json())
      .then((resp) => {
        setStatisticData(resp);
      });
  };

  useEffect(() => {
    fetchDataBySelectedDates();
  }, [startDate, endDate]);

  useEffect(() => {
    fetchPhraseList()
      .then((resp) => resp.json())
      .then(setPhrasesList);
  }, []);

  const displayStatisticData = useMemo(() => {
    if (selectedPhraseId) {
      return statisticData.filter(
        (item) => item.phrase_id.toString() === selectedPhraseId,
      );
    }

    return statisticData;
  }, [statisticData, selectedPhraseId]);

  const onStartChange = (e) => {
    const [year, month, day] = e.target.value.split('-');
    setStartDate(new Date(year, month - 1, day));
  };

  const onEndChange = (e) => {
    const [year, month, day] = e.target.value.split('-');
    const endDate = new Date(year, month - 1, day);

    if (endDate < startDate) {
      setEndDate(startDate);
    } else {
      setEndDate(endDate);
    }
  };

  const onSwitchIsDatesUsed = () => {
    if (isDatesUsed) {
      fetchStatisticFull()
        .then((resp) => resp.json())
        .then((resp) => {
          setStatisticData(resp);
        });
    } else {
      fetchDataBySelectedDates();
    }
    setIsDatesUsed((isUsed) => !isUsed);
  };

  const onClickOnIsDatesUsed = () => {
    setIsDatesUsed(true);
  };

  const onChangePhraseIdFilter = (e) => {
    setSelectedPhraseId(e.target.value);
  };

  const datesSwitchInfinityClassName = `btn ${isDatesUsed ? 'btn-light' : 'btn-info'}`;
  const datesSwitchCalendarClassName = `btn ${isDatesUsed ? 'btn-info' : 'btn-light'}`;

  const datesEndClassName = isDatesUsed ? '' : 'px-4';

  const edgeDates = isDatesUsed
    ? [startDate, endDate]
    : getEdgeDates(statisticData);

  return (
    <div className="d-flex flex-column">
      <div className="mb-2 px-4 d-flex align-items-center justify-content-between">
        <div className="d-flex">
          <div className="px-2">
            <label htmlFor="start-date" className="form-label">
              From date:
            </label>
            <input
              id="start-date"
              type="date"
              className="form-control"
              value={startDateStr}
              disabled={!isDatesUsed}
              onChange={onStartChange}
              onClick={onClickOnIsDatesUsed}
            />
          </div>
          <div className={datesEndClassName} onClick={onClickOnIsDatesUsed}>
            <label htmlFor="end-date" className="form-label">
              To date:
            </label>
            <input
              id="end-date"
              type="date"
              className="form-control"
              value={endDateStr}
              onChange={onEndChange}
              disabled={!isDatesUsed}
            />
          </div>
          <div className="d-flex flex-column justify-content-end mx-2">
            <div className="btn-group">
              <div
                className={datesSwitchCalendarClassName}
                onClick={onSwitchIsDatesUsed}
              >
                <img src={calendarSvg} alt="calendar" />
              </div>
              <div
                className={datesSwitchInfinityClassName}
                onClick={onSwitchIsDatesUsed}
              >
                <img src={infSvg} alt="inf" />
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center">
          <div>
            <select onChange={onChangePhraseIdFilter}>
              <option key="zero" value="">
                All
              </option>
              {phrasesList.map((phraseItem) => (
                <option key={phraseItem.id} value={phraseItem.id}>
                  {phraseItem.phrase}
                </option>
              ))}
            </select>
          </div>
          <div className="mx-4 p-3 badge text-bg-light">
            Total: {displayStatisticData.length}
          </div>
        </div>
      </div>
      <div>
        <StatisticGraph
          data={displayStatisticData}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
          startDate={edgeDates[0]}
          endDate={edgeDates[1]}
        />
      </div>
    </div>
  );
};
